import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FaExclamationTriangle } from 'react-icons/fa';
import styles from './ConfirmDialog.module.css';

/**
 * ConfirmDialog component - Reusable modal dialog for confirmation prompts
 * Replaces browser's window.confirm() with a custom accessible modal
 */
const ConfirmDialog = ({
  show,
  title,
  message,
  confirmLabel = 'Discard Changes',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'danger'
}) => {
  const dialogRef = useRef(null);
  const cancelButtonRef = useRef(null);

  // Handle ESC key press
  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [show, onCancel]);

  // Focus trap within dialog
  useEffect(() => {
    if (!show || !dialogRef.current) return;

    const focusableElements = dialogRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    // Focus cancel button on open
    if (cancelButtonRef.current) {
      cancelButtonRef.current.focus();
    }

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [show]);

  if (!show) return null;

  return (
    <div className={styles.overlay} role="presentation">
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-message"
      >
        <div className={styles.header}>
          <FaExclamationTriangle className={styles.icon} aria-hidden="true" />
          <h3 id="confirm-dialog-title" className={styles.title}>
            {title}
          </h3>
        </div>

        <div className={styles.body}>
          <p id="confirm-dialog-message" className={styles.message}>
            {message}
          </p>
        </div>

        <div className={styles.footer}>
          <button
            ref={cancelButtonRef}
            type="button"
            className={styles.buttonSecondary}
            onClick={onCancel}
            aria-label={cancelLabel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`${styles.button} ${
              variant === 'danger' ? styles.buttonDanger :
              variant === 'warning' ? styles.buttonWarning :
              styles.buttonPrimary
            }`}
            onClick={onConfirm}
            aria-label={confirmLabel}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

ConfirmDialog.propTypes = {
  show: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['danger', 'warning', 'primary'])
};

export default ConfirmDialog;
